import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api, { getBackendBaseUrl } from '@/lib/api';
import { queueOnboardingAfterSignup } from '@/lib/onboardingJourney';
import { persistGoogleAuthSession } from '@/lib/googleAuthOAuth';
import { VideoMosaicBackground } from '@/components/VideoMosaicBackground';
import { TwinneeWordmark } from '@/components/TwinneeWordmark';

export const GoogleAuthCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const exchangeStartedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const finish = async () => {
      if (exchangeStartedRef.current) return;
      const googleError = searchParams.get('google');
      const message = searchParams.get('message');
      if (googleError === 'error') {
        if (!cancelled) setError(message || 'Google sign-in failed');
        return;
      }

      // Google was configured to redirect here instead of the backend callback — forward once.
      const oauthCode = (searchParams.get('code') || '').trim();
      const oauthState = (searchParams.get('state') || '').trim();
      if (oauthCode && oauthState) {
        const backend = getBackendBaseUrl().replace(/\/+$/, '');
        const qs = new URLSearchParams({
          code: oauthCode,
          state: oauthState,
          force_redirect: '1',
        });
        const oauthErr = (searchParams.get('error') || '').trim();
        if (oauthErr) qs.set('error', oauthErr);
        const oauthErrDesc = (searchParams.get('error_description') || '').trim();
        if (oauthErrDesc) qs.set('error_description', oauthErrDesc);
        window.location.replace(`${backend}/api/auth/google/callback?${qs.toString()}`);
        return;
      }

      const exchangeCode = (searchParams.get('exchange_code') || '').trim();
      if (!exchangeCode) {
        if (localStorage.getItem('storycraft_token')) {
          navigate('/dashboard', { replace: true });
          return;
        }
        if (!cancelled) {
          const backendCallback = `${getBackendBaseUrl().replace(/\/+$/, '')}/api/auth/google/callback`;
          setError(
            `This page only works after Google sign-in finishes. Use Login → Continue with Google (do not open this URL directly). In Google Cloud Console, set Authorized redirect URI to ${backendCallback}, not http://localhost:3000/auth/google/callback.`,
          );
        }
        return;
      }

      exchangeStartedRef.current = true;
      try {
        const { data } = await api.post('/auth/google/exchange', { exchange_code: exchangeCode });
        persistGoogleAuthSession({ token: data.token, user: data.user });
        if (data.is_new_user) queueOnboardingAfterSignup(data.user.id);
        toast.success('Signed in with Google');
        navigate('/dashboard', { replace: true });
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.detail || 'Could not complete Google sign-in');
        }
      }
    };

    finish();
    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-[#0a0a0f]">
      <VideoMosaicBackground variant="auth" showTitles={false} />
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md text-center rounded-3xl p-8 border border-white/10 bg-black/55 backdrop-blur-xl"
      >
        <Link to="/" aria-label="Twinnee home" className="inline-flex items-center mb-6">
          <TwinneeWordmark className="h-11" />
        </Link>
        {error ? (
          <>
            <p className="text-slate-300 text-sm mb-6">{error}</p>
            <Link to="/login" className="text-amber-400 font-medium hover:underline">
              Back to sign in
            </Link>
          </>
        ) : (
          <>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-8 h-8 mx-auto border-2 border-amber-500/30 border-t-amber-500 rounded-full"
            />
            <p className="text-slate-400 text-sm mt-4">Completing Google sign-in…</p>
          </>
        )}
      </motion.div>
    </div>
  );
};
